import type { ChatMessage, PetProfileCardPayload, RequestScrollCallback } from '@chewy/chat-library-common';
import { PaginatedSelector } from '@chewy/chat-library-components';
import PetProfileCard from './PetProfileCard';

const MAX_PETS_PER_PAGE = 3;

interface PetProfileCardSelectorProps {
  /**
   * Array of pet profile cards to display
   */
  data: Array<PetProfileCardPayload>;
  /**
   * Layout direction for the cards
   * @default 'vertical'
   */
  direction?: 'horizontal' | 'vertical';
  /**
   * Chat message associated with the orders
   */
  message?: ChatMessage;
  /**
   * Additional CSS class name
   */
  className?: string;
  /**
   * Page offset for the cards
   * @default 3
   */
  pageOffset?: number;
  /**
   * Callback function to request scrolling to a selected item
   */
  requestScroll?: RequestScrollCallback;
}

/**
 * Displays a list of selectable PetProfileCards with pagination
 * Uses PaginatedSelector to handle pagination and action buttons
 */
export default function PetProfileCardSelector({
  data,
  direction = 'vertical',
  message,
  className,
  pageOffset = MAX_PETS_PER_PAGE,
  requestScroll
}: PetProfileCardSelectorProps) {
  return (
    <PaginatedSelector
      data={data}
      renderItem={(pet, index, { ref, handleSelect }) => (
        <PetProfileCard
          key={pet.id || index}
          ref={ref}
          data={[pet]}
          onSelect={() => handleSelect(message?.responseTimestamp)}
          isSelectable={true}
        />
      )}
      direction={direction}
      className={className}
      pageSize={pageOffset}
      itemType="pet"
      requestScroll={requestScroll}
      showMoreAction={[
        {
          defaultAction: {
            type: 'button',
            text: 'Show More',
            payload: {
              action: 'SHOW_MORE_PETS'
            }
          }
        }
      ]}
      secondaryAction={[
        {
          defaultAction: {
            type: 'button',
            text: 'Need help with a different pet',
            payload: {
              action: 'SHOW_DIFFERENT_PET_OPTIONS'
            }
          }
        }
      ]}
    />
  );
}
